import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormControl, FormBuilder } from '@angular/forms';
import { Business } from 'src/app/models/services/audiences/tiktok/business';
import { Audience } from 'src/app/models/services/audiences/tiktok/audience';
import { Advertiser } from 'src/app/models/services/audiences/tiktok/advertiser';
import { TikTokAudiencesService } from 'src/app/services/audiences/tiktok-audiences.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-tiktok-attributes',
  templateUrl: './tiktok-attributes.component.html',
  styleUrls: ['./tiktok-attributes.component.sass']
})
export class TikTokAttributesComponent implements OnInit {

  @ViewChild('matAutocompleteBusinesses') inputAutocompleteBusinesses!: ElementRef<HTMLInputElement>;
  @ViewChild('matAutocompleteAdvertisers') inputAutocompleteAdvertisers!: ElementRef<HTMLInputElement>;
  @ViewChild('matAutocompleteAudiences') inputAutocompleteAudiences!: ElementRef<HTMLInputElement>;


  @Input() form!: FormGroup;
  @Input() updateValueAndValidity: () => void = () => {};

  businesses: Business[] = [];
  filteredBusinesses: Business[] = [];
  advertisers: Advertiser[] = [];
  filteredAdvertisers: Advertiser[] = [];
  audiences: Audience[] = [];
  filteredAudiences: Audience[] = [];

  audienceSubTypes = [
    { value: 'NORMAL', label: 'Normal' },
    { value: 'REACH_FREQUENCY', label: 'Reach & Frequency' },
  ] as const;

  constructor(
    private tiktokAudiencesServices: TikTokAudiencesService,
    private formBuilder: FormBuilder,
    private spinnerService: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.spinnerService.show('dialogSpinner');

    // Default: Existing audience
    this.form.controls.newAudienceData.setValue('0');

    if (!this.form.contains('audienceSubType')) {
      this.form.addControl('audienceSubType', new FormControl('NORMAL', Validators.required));
    }

    this.tiktokAudiencesServices.getBusiness().subscribe(resp => {
      this.businesses = resp;
      this.onNewAudienceSelectedChange();
      this.spinnerService.hide('dialogSpinner');
    });
  }

  // CHANGE SELECTED (0 existing, 1 new, 2 download)
  onNewAudienceSelectedChange = (): void => {
    const mode = String(this.form.controls.newAudienceData.value); // '0' | '1' | '2'

    // Reset selección de audiencia
    this.form.controls.audienceId?.setValue(null, { emitEvent: false });
    this.form.controls.audienceName?.setValue('', { emitEvent: false });
    if (this.inputAutocompleteAudiences) {
      this.inputAutocompleteAudiences.nativeElement.value = '';
    }

    // IMPORTANTE: en modo '2' NO podemos volver a poner validators required,
    // porque el padre necesita que solo sea obligatorio transferName.
    if (mode === '1') {
      // New audience
      this.form.controls.audienceName?.setValidators([Validators.required, Validators.minLength(2)]);
      this.form.controls.audienceId?.clearValidators();
      this.form.controls.audienceSubType?.setValidators([Validators.required]);
    } else if (mode === '0') {
      // Existing audience
      this.form.controls.audienceId?.setValidators([Validators.required]);
      this.form.controls.audienceName?.clearValidators();
      // audienceSubType puede mantenerse required o no; lo dejamos como está
      this.form.controls.audienceSubType?.setValidators([Validators.required]);
    } else {
      // Download file
      this.form.controls.audienceId?.clearValidators();
      this.form.controls.audienceName?.clearValidators();
      this.form.controls.audienceSubType?.clearValidators();
    }

    this.form.controls.audienceId?.updateValueAndValidity({ emitEvent: false });
    this.form.controls.audienceName?.updateValueAndValidity({ emitEvent: false });
    this.form.controls.audienceSubType?.updateValueAndValidity({ emitEvent: false });

    this.updateValueAndValidity?.();
  };

  // BUSINESS
  onBusinessChange = ($event: MatAutocompleteSelectedEvent): void => {
    this.spinnerService.show('dialogSpinner');

    this.resetAdvertiserControls([]);
    this.tiktokAudiencesServices.getAdvertisers($event.option.value).subscribe(resp => {
      this.advertisers = resp;

      this.spinnerService.hide('dialogSpinner');
      this.inputAutocompleteBusinesses?.nativeElement.blur();
    });
  }

  getBusinessDescription = (id?: string): string => {
    return this.businesses.find(x => x.id == id)?.name || '';
  }

  resetBusinessesFilter = (): void => {
    this.inputAutocompleteBusinesses.nativeElement.value = '';
    this.resetBusinessControls(this.businesses);
  }

  filterBusinesses = (): void => {
    const filterValue = (this.inputAutocompleteBusinesses.nativeElement.value || '').toLowerCase();
    this.resetBusinessControls(
      this.businesses.filter(o => this.matchTerm(filterValue, o.id, o.name))
    );
  }

  resetBusinessControls = (filteredBusinesses: Business[]): void => {
    this.filteredBusinesses = filteredBusinesses;

    if (filteredBusinesses === this.businesses) {
      this.form.controls.businessId?.setValue(null);
    }

    this.inputAutocompleteAdvertisers && (this.inputAutocompleteAdvertisers.nativeElement.value = '');

    this.advertisers = [];
    this.resetAdvertiserControls([]);
  }

  // BUSINESS -> ADVERTISERS
  onAdvertiserChange = ($event?: MatAutocompleteSelectedEvent): void => {
    const mode = String(this.form.controls.newAudienceData.value); // '0' | '1' | '2'
    const advertiserId = $event?.option?.value;

    if (advertiserId == null) return;
    this.resetAudiencesControls([]);
    if (mode === '0') {
      this.spinnerService.show('dialogSpinner');
      this.tiktokAudiencesServices.getAudiences(advertiserId).subscribe(resp => {
        this.audiences = resp.filter(x => !x.isExpiring);
        this.spinnerService.hide('dialogSpinner');
        this.inputAutocompleteAdvertisers.nativeElement.blur();
        this.updateValueAndValidity?.();
      });
    }
  };

  getAdvertiserDescription = (advertiserId: any): string => {
    const advertiser = this.advertisers.find(x => x.id == advertiserId);
    if (!advertiser) return '';
    return advertiser.name && advertiser.name.trim() ? advertiser.name : advertiser.id;
  };

  resetAdvertisersFilter = (): void => {
    this.inputAutocompleteAdvertisers.nativeElement.value = '';
    this.resetAdvertiserControls(this.advertisers);
  };

  filterAdvertisers = (): void => {
    const term = this.inputAutocompleteAdvertisers.nativeElement.value || '';
    const filtered = this.advertisers.filter(o => this.matchTerm(term, o.id, o.name));
    this.resetAdvertiserControls(this.filteredAdvertisers = filtered);
  };

  resetAdvertiserControls = (filteredAdvertisers: Advertiser[]): void => {
    this.filteredAdvertisers = filteredAdvertisers;

    if (filteredAdvertisers === this.advertisers || filteredAdvertisers.length === 0) {
      this.form.controls.advertiserId?.setValue(null);
    }

    this.inputAutocompleteAudiences && (this.inputAutocompleteAudiences.nativeElement.value = '');
    this.audiences = [];
    this.resetAudiencesControls([]);
  };

  // ADVERTISERS -> AUDIENCES
  onAudienceChange = ($event?: MatAutocompleteSelectedEvent): void => {
    if (!$event?.option?.value) return;

    this.inputAutocompleteAdvertisers.nativeElement.blur();
    this.form.controls.audienceId?.setValue($event.option.value);
    this.form.controls.audienceName?.setValue(this.getAudienceDescription($event.option.value));
    this.updateValueAndValidity?.();
  };

  getAudienceDescription = (audienceId: string): string => {
    return this.audiences.find(x => x.id === audienceId)?.name || '';
  }

  resetAudiencesFilter = (): void => {
    this.inputAutocompleteAudiences.nativeElement.value = '';
    this.resetAudiencesControls(this.audiences);
  }

  filterAudiences = (): void => {
    const filterValue = (this.inputAutocompleteAudiences.nativeElement.value || '').toLowerCase();
    this.resetAudiencesControls(
      this.audiences.filter(o => `${o.id} - ${o.name}`.toLowerCase().includes(filterValue))
    );

    if (filterValue !== (this.form.controls.audienceName?.value || '').toLowerCase()) {
      this.form.controls.audienceName?.setValue(null);
      this.updateValueAndValidity?.();
    }
  }

  onAudienceDescriptionChange = (): void => {
    this.updateValueAndValidity?.();
  }

  resetAudiencesControls = (filteredAudiences: Audience[]): void => {
    this.filteredAudiences = filteredAudiences;

    if (filteredAudiences === this.audiences || filteredAudiences.length === 0) {
      this.form.controls.audienceId?.setValue(null);
    }

    this.updateValueAndValidity?.();
  }

  getAdvertiserTooltip(adv: Advertiser): string {
    if (adv?.name) return adv.name;
    const byAll = this.advertisers?.find(a => a.id === adv?.id)?.name;
    if (byAll) return byAll;

    return adv?.id || '';
  }

  private matchTerm(term: string, ...fields: Array<string | number | null | undefined>): boolean {
    const t = (term ?? '').toString().toLowerCase().trim();
    if (!t) return true;
    return fields.some(f => (f ?? '').toString().toLowerCase().includes(t));
  }
}
